import { Box, Button, InputAdornment, MenuItem, Select, type SelectChangeEvent, TextField } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { DataGridPro, type GridColDef } from "@mui/x-data-grid-pro";
import { type ChangeEvent, useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { DateTime } from "luxon";

import { FailedRetry } from "@/components/failed-retry";
import { type FilterType } from "@/api/walmart-connector-api";
import { type OrderAudit } from "@/schemas";
import { EmptyOrdersView } from "@/views/configuration/orders-audit/components/empty-orders-view";
import { OrderDetailsModal } from "@/views/configuration/orders-audit/components/order-details-modal";
import { ConfigurationPageLayout } from "@/views/configuration/components/configuration-page-layout";

import { getLatestEvent } from "./utils/date-utils";
import { useOrdersAudit } from "./use-orders-audit";

export const OrdersAudit = () => {
	const intl = useIntl();
	const {
		filter,
		setFilter,
		filterType,
		setFilterType,
		data,
		handleSearch,
		isLoading,
		refetch,
		isError,
		onDisplayOrderDetails,
		modalData,
		onCloseDetailsModal,
		isSearching,
		timeZone,
	} = useOrdersAudit();

	const columns = useMemo<GridColDef<OrderAudit>[]>(
		() => [
			{
				field: "firstEventDate",
				headerName: intl.formatMessage({ id: "orders_audit.first_event_date", defaultMessage: "First Event Date" }),
				width: 250,
				valueGetter: (_value, row) =>
					DateTime.fromISO(row.firstEventDate).setZone(timeZone).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
			},
			{
				field: "lastEventDate",
				headerName: intl.formatMessage({ id: "orders_audit.last_event_date", defaultMessage: "Last Event Date" }),
				width: 250,
				valueGetter: (_value, row) =>
					DateTime.fromISO(row.lastEventDate).setZone(timeZone).toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS),
			},
			{
				field: "correlationId",
				headerName: intl.formatMessage({ id: "orders_audit.correlation_id", defaultMessage: "Correlation Id" }),
				flex: 1,
				valueGetter: (_value, row) => {
					const latestEvent = getLatestEvent(row.events);
					return latestEvent?.correlationId ?? "";
				},
			},
			{
				field: "orderId",
				headerName: intl.formatMessage({ id: "orders_audit.order_id", defaultMessage: "Order Id" }),
				flex: 1,
			},
			{
				field: "lastEvent",
				headerName: intl.formatMessage({ id: "orders_audit.last_event", defaultMessage: "Last Event" }),
				flex: 1,
				valueGetter: (_value, row) => {
					const latestEvent = getLatestEvent(row.events);
					return latestEvent?.eventType ?? "";
				},
			},
			{
				field: "actions",
				headerName: intl.formatMessage({ id: "orders_audit.view_events", defaultMessage: "View Events" }),
				width: 140,
				sortable: false,
				renderCell: (params) => (
					<Button variant="contained" size="small" disableElevation onClick={() => onDisplayOrderDetails(params.row)}>
						<FormattedMessage id="orders_audit.view" defaultMessage="View" />
					</Button>
				),
			},
		],
		[intl, onDisplayOrderDetails, timeZone],
	);

	const filterOptions = useMemo<{ key: string; text: string; value: FilterType }[]>(
		() => [
			{
				key: "CorrelationId",
				text: intl.formatMessage({ id: "orders_audit.correlation_id", defaultMessage: "Correlation Id" }),
				value: "CorrelationId",
			},
			{
				key: "OrderId",
				text: intl.formatMessage({ id: "orders_audit.order_id", defaultMessage: "Order Id" }),
				value: "OrderId",
			},
			{
				key: "PickTicketId",
				text: intl.formatMessage({ id: "orders_audit.pick_ticket_id", defaultMessage: "Pick Ticket Id" }),
				value: "PickTicketId",
			},
		],
		[intl],
	);

	// The grid slot must capture the current query state.
	const NoRowsOverlay = useCallback(
		// eslint-disable-next-line @eslint-react/no-nested-component-definitions
		() => (
			<Box display="flex" justifyContent="center" alignItems="center" height="100%">
				{isError === true && <FailedRetry retry={refetch} />}
				{isError === false && <EmptyOrdersView />}
			</Box>
		),
		[isError, refetch],
	);

	return (
		<ConfigurationPageLayout
			title={intl.formatMessage({ id: "orders_audit.title", defaultMessage: "Orders Audit" })}
			actions={
				<>
					<TextField
						value={filter}
						onChange={(e: ChangeEvent<HTMLInputElement>) => setFilter(e.target.value)}
						sx={{
							minWidth: 450,
							backgroundColor: "background.paper",
							"& .MuiInputAdornment-root .MuiOutlinedInput-notchedOutline": {
								borderRight: (theme) => `1px solid ${theme.palette.divider} !important`,
							},
							borderRadius: 1,
						}}
						size="small"
						slotProps={{
							input: {
								sx: { pl: 0 },
								startAdornment: (
									<InputAdornment position="start">
										<Select
											value={filterType}
											size="small"
											onChange={(e: SelectChangeEvent) => setFilterType(e.target.value as FilterType)}
											sx={{
												minWidth: 150,
												borderTopRightRadius: 0,
												borderBottomRightRadius: 0,
												"& .MuiOutlinedInput-notchedOutline, &:hover .MuiOutlinedInput-notchedOutline, &.Mui-focused .MuiOutlinedInput-notchedOutline":
													{
														border: "none",
														borderRight: (theme) => `1px solid ${theme.palette.divider}`,
													},
											}}
											IconComponent={KeyboardArrowDownIcon}
										>
											{filterOptions.map((option) => (
												<MenuItem key={option.key} value={option.value}>
													{option.text}
												</MenuItem>
											))}
										</Select>
									</InputAdornment>
								),
							},
						}}
						variant="outlined"
					/>
					<Button variant="contained" disableElevation onClick={handleSearch} disabled={isSearching || !filter}>
						<FormattedMessage id="orders_audit.search" defaultMessage="Search" />
					</Button>
				</>
			}
		>
			<DataGridPro
				rows={data}
				columns={columns}
				loading={isLoading}
				getRowId={(row) => row.orderId}
				disableRowSelectionOnClick
				hideFooter
				getRowHeight={() => "auto"}
				disableColumnReorder
				disableColumnMenu
				disableColumnSelector
				slots={{
					noRowsOverlay: NoRowsOverlay,
				}}
				sx={{
					"& .MuiDataGrid-cell": {
						display: "flex",
						alignItems: "center",
						wordWrap: "break-word",
					},
				}}
			/>

			<OrderDetailsModal onClose={onCloseDetailsModal} orderAudit={modalData} timeZone={timeZone ?? "UTC"} />
		</ConfigurationPageLayout>
	);
};
